import React from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    CircularProgress,
    Divider,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import RefreshIcon from '@mui/icons-material/Refresh';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import DateRangePicker from '../../../components/DateRangePicker';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { fetchBranchLedger, setFilters } from '../../../store/slices/branchFinanceSlice';
import { BranchLedgerEntry } from '../types/branchFinance.types';
import { API_BASE_URL } from '../../../services/apiUtils';

interface BranchLedgerTabProps {
    selectedBranchId: number;
}

const formatCurrency = (value: number) =>
    `Rs. ${value.toLocaleString('en-PK', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const BranchLedgerTab: React.FC<BranchLedgerTabProps> = ({ selectedBranchId }) => {
    const dispatch = useAppDispatch();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const { ledger, loading, error } = useAppSelector((state) => state.branchFinance);

    const [dateRange, setDateRange] = React.useState<{ from?: string; to?: string }>({});
    const [exportError, setExportError] = React.useState<string | null>(null);

    React.useEffect(() => {
        setDateRange({});
        dispatch(setFilters({ fromDate: undefined, toDate: undefined }));
    }, [selectedBranchId, dispatch]);

    React.useEffect(() => {
        if (!selectedBranchId) {
            return;
        }
        dispatch(
            fetchBranchLedger({
                branchId: selectedBranchId,
                fromDate: dateRange.from,
                toDate: dateRange.to,
            })
        );
    }, [dispatch, selectedBranchId, dateRange.from, dateRange.to]);

    const handleDateChange = (from: string, to: string) => {
        setDateRange({ from, to });
        dispatch(setFilters({ fromDate: from, toDate: to }));
    };

    const handleDateClear = () => {
        setDateRange({});
        dispatch(setFilters({ fromDate: undefined, toDate: undefined }));
    };

    const handleRefresh = () => {
        if (!selectedBranchId) return;
        dispatch(
            fetchBranchLedger({
                branchId: selectedBranchId,
                fromDate: dateRange.from,
                toDate: dateRange.to,
            })
        );
    };

    const handleExport = () => {
        if (!selectedBranchId) return;
        setExportError(null);
        try {
            const queryParams = new URLSearchParams();
            queryParams.append('branch_id', String(selectedBranchId));
            if (dateRange.from) {
                queryParams.append('from_date', dateRange.from);
            }
            if (dateRange.to) {
                queryParams.append('to_date', dateRange.to);
            }
            
            // Get auth token and add it to the URL
            const token = localStorage.getItem('token');
            if (token) {
                queryParams.append('token', token);
            }
            
            const exportUrl = `${API_BASE_URL}/public/branch-ledger/export?${queryParams.toString()}`;
            window.open(exportUrl, '_blank', 'noopener,noreferrer');
        } catch (err) {
            setExportError(err instanceof Error ? err.message : 'Failed to export ledger');
        }
    };

    const renderEntryRow = (entry: BranchLedgerEntry) => (
        <TableRow key={entry.id}>
            <TableCell>{entry.description}</TableCell>
            <TableCell>{entry.date || '-'}</TableCell>
            <TableCell>{entry.user?.name || 'N/A'}</TableCell>
            <TableCell align="right">{entry.debit ? formatCurrency(entry.debit) : '-'}</TableCell>
            <TableCell align="right">{entry.credit ? formatCurrency(entry.credit) : '-'}</TableCell>
            <TableCell align="right">{formatCurrency(entry.balance)}</TableCell>
        </TableRow>
    );

    const renderEntryCard = (entry: BranchLedgerEntry) => (
        <Card key={entry.id} variant="outlined">
            <CardContent>
                <Stack spacing={1}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle2" color="text.secondary">
                            {entry.date || '-'}
                        </Typography>
                        <Typography variant="subtitle2" fontWeight="bold">
                            {formatCurrency(entry.balance)}
                        </Typography>
                    </Box>
                    <Typography variant="body1">{entry.description}</Typography>
                    <Typography variant="caption" color="text.secondary">
                        {entry.user?.name || 'N/A'}
                    </Typography>
                    <Divider />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Box>
                            <Typography variant="caption" color="text.secondary">
                                Debit
                            </Typography>
                            <Typography variant="body2" color={entry.debit ? 'error.main' : 'text.primary'}>
                                {entry.debit ? formatCurrency(entry.debit) : '-'}
                            </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="caption" color="text.secondary">
                                Credit
                            </Typography>
                            <Typography variant="body2" color={entry.credit ? 'success.main' : 'text.primary'}>
                                {entry.credit ? formatCurrency(entry.credit) : '-'}
                            </Typography>
                        </Box>
                    </Box>
                </Stack>
            </CardContent>
        </Card>
    );

    return (
        <Card sx={{ overflow: 'hidden' }}>
            <CardHeader
                title="Branch Ledger"
                subheader={ledger ? `Financial activity for ${ledger.branch.name}` : undefined}
                action={
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
                        <DateRangePicker
                            fromDate={dateRange.from}
                            toDate={dateRange.to}
                            onDateChange={handleDateChange}
                            onClear={handleDateClear}
                            variant="inline"
                        />
                        <Stack direction="row" spacing={1}>
                            <IconButton onClick={handleRefresh} disabled={loading}>
                                <RefreshIcon />
                            </IconButton>
                            <Button
                                variant="contained"
                                onClick={handleExport}
                                startIcon={<FileDownloadIcon />}
                                disabled={!ledger}
                            >
                                Export PDF
                            </Button>
                        </Stack>
                    </Stack>
                }
            />
            <CardContent>
                {exportError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {exportError}
                    </Alert>
                )}
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                )}
                {!loading && !ledger && (
                    <Alert severity="info">Select a branch to load ledger details.</Alert>
                )}
                {ledger && (
                    <Stack spacing={3}>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                    <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Branch
                                        </Typography>
                                        <Typography variant="h6">{ledger.branch.name}</Typography>
                                        {ledger.branch.city?.name && (
                                            <Typography variant="body2" color="text.secondary">
                                                City: {ledger.branch.city.name}
                                            </Typography>
                                        )}
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 'auto', pt: 1 }}>
                                            Range: {ledger.dateRange.from || '—'} - {ledger.dateRange.to || '—'}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                    <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Total Debit
                                        </Typography>
                                        <Typography variant="h5" color="error" sx={{ flex: 1 }}>
                                            {formatCurrency(ledger.totals.debit)}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                    <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Total Credit
                                        </Typography>
                                        <Typography variant="h5" color="success.main">
                                            {formatCurrency(ledger.totals.credit)}
                                        </Typography>
                                        <Divider sx={{ my: 1.5 }} />
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Final Balance
                                        </Typography>
                                        <Typography variant="h6">
                                            {formatCurrency(ledger.totals.finalBalance)}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>

                        {ledger.entries.length === 0 ? (
                            <Alert severity="info">No ledger entries found for the selected filters.</Alert>
                        ) : isMobile ? (
                            <Stack spacing={2}>
                                {ledger.entries.map(renderEntryCard)}
                            </Stack>
                        ) : (
                            <Box sx={{ overflowX: 'auto' }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Description</TableCell>
                                            <TableCell>Date</TableCell>
                                            <TableCell>User</TableCell>
                                            <TableCell align="right">Debit</TableCell>
                                            <TableCell align="right">Credit</TableCell>
                                            <TableCell align="right">Balance</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {ledger.entries.map(renderEntryRow)}
                                        <TableRow>
                                            <TableCell colSpan={3} sx={{ fontWeight: 600 }}>
                                                Totals
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                                                {formatCurrency(ledger.totals.debit)}
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                                                {formatCurrency(ledger.totals.credit)}
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                                                {formatCurrency(ledger.totals.finalBalance)}
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </Box>
                        )}
                    </Stack>
                )}
            </CardContent>
        </Card>
    );
};

export default BranchLedgerTab;

